import { VOCAB_LABELS } from '@dyeatlas/shared'
import { useQuery } from '@tanstack/react-query'
import { Lock, Users } from 'lucide-react'
import { NavLink, Navigate, Route, Routes } from 'react-router-dom'
import { MasterDataTable } from './MasterDataTable'
import { ReferenceList } from './ReferenceList'
import { UserManagement } from './UserManagement'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

/** Shown alphabetically, which is how administrators look for a list. */
export const MASTER_SECTIONS = [
  { slug: 'dye-sources', kind: 'dyeSource', label: 'Dye sources' },
  { slug: 'drying-methods', kind: 'dryingMethod', label: VOCAB_LABELS.dryingMethod.plural },
  { slug: 'extraction-methods', kind: 'extractionMethod', label: VOCAB_LABELS.extractionMethod.plural },
  { slug: 'fibers', kind: 'fiber', label: VOCAB_LABELS.fiber.plural },
  { slug: 'filtration-methods', kind: 'filtration', label: VOCAB_LABELS.filtration.plural },
  { slug: 'illuminants', kind: 'illuminant', label: VOCAB_LABELS.illuminant.plural },
  { slug: 'institutions', kind: 'institution', label: VOCAB_LABELS.institution.plural },
  { slug: 'manuscripts', kind: 'manuscript', label: 'Manuscripts' },
  { slug: 'mordants', kind: 'mordant', label: VOCAB_LABELS.mordant.plural },
  { slug: 'pre-treatments', kind: 'preTreatment', label: VOCAB_LABELS.preTreatment.plural },
  { slug: 'references', kind: 'reference', label: 'References' },
  { slug: 'standard-observers', kind: 'observer', label: VOCAB_LABELS.observer.plural },
  { slug: 'water-types', kind: 'waterType', label: VOCAB_LABELS.waterType.plural },
  { slug: 'weave-structures', kind: 'weaveStructure', label: VOCAB_LABELS.weaveStructure.plural },
  { slug: 'yarn-types', kind: 'yarnType', label: VOCAB_LABELS.yarnType.plural },
] as const

export type MasterSection = (typeof MASTER_SECTIONS)[number]

export function Admin() {
  const { data: summary } = useQuery({
    queryKey: ['admin', 'master-data', 'summary'],
    queryFn: () => api.get<Record<string, number>>('/admin/master-data/summary'),
  })

  return (
    <div className="flex gap-7">
      <aside className="w-56 shrink-0">
        <h1 className="font-display text-xl font-semibold text-ink">Master data</h1>
        <p className="mt-1 text-[13px] text-ink-muted">Controlled lists used across DyeAtlas.</p>

        <nav className="mt-5 space-y-0.5">
          {MASTER_SECTIONS.map((section) => (
            <SideLink
              key={section.slug}
              to={`/admin/${section.slug}`}
              count={summary?.[section.kind]}
            >
              {section.label}
            </SideLink>
          ))}
        </nav>

        <div className="my-5 h-px bg-line" />

        <SideLink to="/admin/users" icon={<Users className="size-4" />}>
          User management
        </SideLink>

        <div className="mt-5 rounded-[var(--radius-control)] border border-line bg-paper-muted p-3">
          <p className="flex items-center gap-1.5 text-[12px] font-semibold text-ink-soft">
            <Lock className="size-3.5" />
            Admin-only vocabulary
          </p>
          <p className="mt-1 text-[12px] leading-relaxed text-ink-muted">
            Only admins can add or edit canonical names. Researchers select from these lists.
          </p>
        </div>
      </aside>

      <section className="min-w-0 flex-1">
        <Routes>
          <Route index element={<Navigate to="/admin/dye-sources" replace />} />
          {MASTER_SECTIONS.filter((section) => section.kind !== 'reference').map((section) => (
            <Route
              key={section.slug}
              path={section.slug}
              element={<MasterDataTable section={section} />}
            />
          ))}
          <Route path="references" element={<ReferenceList />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="*" element={<Navigate to="/admin/dye-sources" replace />} />
        </Routes>
      </section>
    </div>
  )
}

function SideLink({
  to,
  count,
  icon,
  children,
}: {
  to: string
  count?: number
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex items-center justify-between gap-2 rounded-[var(--radius-control)] px-3 py-2 text-[13px] transition-colors',
          isActive
            ? 'bg-brand-soft font-semibold text-brand'
            : 'text-ink-soft hover:bg-paper-muted hover:text-ink',
        )
      }
    >
      <span className="flex items-center gap-2">
        {icon}
        {children}
      </span>
      {count !== undefined && <span className="text-[12px] text-ink-faint">{count}</span>}
    </NavLink>
  )
}
